import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, TrendingDown, PiggyBank } from "lucide-react";
import axios from "axios";
import { scrollTo } from "./Header";
import { API } from "../../lib/api";
const fmt = (n) => Math.round(n).toLocaleString("de-CH");

const Anim = ({ value, testid, className }) => (
  <motion.span
    key={value}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    data-testid={testid}
    className={`inline-block ${className}`}
  >
    CHF {fmt(value)}
  </motion.span>
);

export default function SavingsCalculator() {
  const [orders, setOrders] = useState(300);
  const [avg, setAvg] = useState(38);
  const [rate, setRate] = useState(30);
  const [cfg, setCfg] = useState({ apps_price: 3980, annual_price: 380, discount_percent: 0, discount_active: false });

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const disc = cfg.discount_active && cfg.discount_percent > 0 ? cfg.discount_percent / 100 : 0;
  const appsPrice = disc ? cfg.apps_price * (1 - disc) : cfg.apps_price;
  const monthlyLoss = orders * avg * (rate / 100);
  const fiveYearLoss = monthlyLoss * 60;
  const ownerCost = appsPrice + cfg.annual_price * 5;
  const savings = Math.max(fiveYearLoss - ownerCost, 0);

  return (
    <section id="proof" data-testid="proof-section" className="max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
      >
        The Proof
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1 }}
        className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl text-ink"
      >
        See what the platforms <span className="text-outline">take from you.</span>
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="mt-16 rounded-2xl border border-ink/10 bg-white p-8 lg:p-14 card-glow grid grid-cols-1 lg:grid-cols-2 gap-14"
      >
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink/40 mb-8">Your numbers</div>
          <div className="space-y-9">
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-sm text-ink/70 font-light">Platform orders per month</label>
                <span data-testid="calc-orders-value" className="font-mono text-sm text-brand">{orders}</span>
              </div>
              <input data-testid="calc-orders-slider" type="range" min="50" max="2000" step="25" value={orders} onChange={(e) => setOrders(+e.target.value)} className="w-full accent-[#D10056]" />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-sm text-ink/70 font-light">Average order value</label>
                <span data-testid="calc-avg-value" className="font-mono text-sm text-brand">CHF {avg}</span>
              </div>
              <input data-testid="calc-avg-slider" type="range" min="15" max="80" step="1" value={avg} onChange={(e) => setAvg(+e.target.value)} className="w-full accent-[#D10056]" />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="text-sm text-ink/70 font-light">Platform commission</label>
                <span data-testid="calc-rate-value" className="font-mono text-sm text-brand">{rate}%</span>
              </div>
              <div className="flex gap-2">
                {[15, 30, 35].map((r) => (
                  <button
                    key={r}
                    data-testid={`calc-rate-${r}`}
                    onClick={() => setRate(r)}
                    className={`flex-1 py-2.5 rounded-full font-mono text-xs transition-colors duration-300 ${rate === r ? "bg-brand text-white" : "border border-ink/15 text-ink/60 hover:border-brand hover:text-brand"}`}
                  >
                    {r}%
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink/40 mb-8">The damage — and the alternative</div>
          <div className="flex items-start gap-4">
            <TrendingDown className="w-6 h-6 text-brand shrink-0 mt-1" />
            <div>
              <div className="text-sm text-ink/60 font-light">The platforms take from you, every month</div>
              <Anim value={monthlyLoss} testid="calc-monthly-loss" className="font-display font-black text-4xl lg:text-5xl text-brand tracking-tight" />
            </div>
          </div>
          <div className="mt-7 flex items-baseline justify-between border-t border-ink/10 pt-5">
            <span className="text-sm text-ink/60 font-light">Over 5 years, that is</span>
            <Anim value={fiveYearLoss} testid="calc-five-year-loss" className="font-display font-bold text-2xl text-ink" />
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-sm text-ink/60 font-light">simplyOwner, 5 years total (apps package + running costs)</span>
            <Anim value={ownerCost} testid="calc-owner-cost" className="font-display font-bold text-2xl text-ink" />
          </div>
          <div className="mt-8 rounded-xl bg-black text-white p-7 flex items-center gap-5">
            <PiggyBank className="w-9 h-9 shrink-0 text-ice" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ice">You keep more over 5 years</div>
              <Anim value={savings} testid="calc-savings" className="font-display font-black text-4xl lg:text-5xl tracking-tight" />
            </div>
          </div>
          <button
            data-testid="calc-cta"
            onClick={() => scrollTo("#contact")}
            className="group mt-8 flex items-center justify-center gap-3 bg-ink hover:bg-brand text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-full transition-colors duration-300"
          >
            Keep my money
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </motion.div>
    </section>
  );
}
